import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { CHART_SOURCE, getPeriodType } from '../chartProtocol';
import {
  CandlestickColors,
  ChartTypeKey,
  ComparisonItem,
  DrawingToolConfig,
  DrawingToolKey,
  IntervalKey,
  LineConfig,
  PeriodKey,
  TAItem,
  ViewState,
} from '../types';
import useChartData, {
  calcFromDate,
  formatDateYMD,
} from '../../../hooks/data/useChartData';
import Loading from '../../../Components/Loading/Loading';
import ErrorMessage from '../../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../../constants/common.constant';
import {
  getChartHtsData,
  getChartMtsData,
} from '../../../api/advanceChart.api';
import {
  buildDrawToolOption,
  buildMessage,
  buildTAPayload,
  getDrawTool,
  OVERLAY_IDS,
} from '../chartSerializer';
import { DEFAULT_DRAWING_CONFIG, DrawingToolsList } from '../constants';

const DEFAULT_DRAWING_CONFIGS = DrawingToolsList.reduce(
  (acc, { key }) => ({ ...acc, [key]: { ...DEFAULT_DRAWING_CONFIG } }),
  {} as Record<DrawingToolKey, DrawingToolConfig>,
);

const DEFAULT_CHART_BUNDLE_URL = (() => {
  if (typeof process !== 'undefined' && process.env?.PUBLIC_URL) {
    return process.env.PUBLIC_URL + '/chart-bundle/index.html';
  }
  if (typeof document !== 'undefined') {
    const baseTag = document.querySelector('base');
    if (baseTag?.href) {
      const basePath = new URL(baseTag.href).pathname.replace(/\/$/, '');
      return basePath + '/chart-bundle/index.html';
    }
  }
  return '/chart-bundle/index.html';
})();

const findScrollableAncestor = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

export interface DrawMenu {
  color: string; // hex WITHOUT #
  dash: string; // 's' | 'd' | 't'
  thickness: number;
  mode: number;
}

export interface ChartViewHandle {
  sendToChart: (payload: Record<string, unknown>) => void;
  /** Prints only the chart iframe's document. Returns false when the bundle is
   *  cross-origin (its document can't be reached), so the caller can fall back. */
  print: () => boolean;
}

const PRINT_PAGE_STYLE_ID = 'chart-print-page-style';
const PX_PER_MM = 96 / 25.4;

interface ChartViewProps {
  symbol: string;
  token?: string;
  interval: IntervalKey;
  period: PeriodKey;
  chartType: ChartTypeKey;
  candlestickColors: CandlestickColors;
  lineConfig: LineConfig;
  barColor?: string;
  activeTA: TAItem[];
  comparisonList: ComparisonItem[];
  comparisonMode: boolean;
  view: ViewState;
  activeTool?: DrawingToolKey | null;
  moveEnabled?: boolean;
  drawingConfigs?: Record<DrawingToolKey, DrawingToolConfig>;
  chartBundleUrl?: string;
  iframeClassName?: string;
  blockPointerEvents?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
  onNoData?: () => void;
  onError?: (error: { message: string; type: string }) => void;
  onDrawMenuChange?: (menu: DrawMenu | null) => void;
}

const ChartView = React.forwardRef<ChartViewHandle, ChartViewProps>(
  (props, ref) => {
    const activeTool = props.activeTool ?? null;
    const moveEnabled = props.moveEnabled ?? false;
    const drawingConfigs = props.drawingConfigs ?? DEFAULT_DRAWING_CONFIGS;
    const chartBundleUrl = props.chartBundleUrl || DEFAULT_CHART_BUNDLE_URL;
    const bundleUrlRef = useRef(`${chartBundleUrl}?t=${Date.now()}`);
    const bundleUrl = bundleUrlRef.current;
    const iframeRef = useRef<HTMLIFrameElement>(null);
    // Bumped on every 'ready' from the bundle — a reloaded iframe re-announces
    // ready, and the bump makes the send effect push the full payload again
    const [readyVersion, setReadyVersion] = useState(0);
    const isChartReady = readyVersion > 0;
    const isInitializedRef = useRef(false);
    const suppressDrawMenuRef = useRef(false);
    const activeToolRef = useRef(activeTool);
    const moveEnabledRef = useRef(moveEnabled);
    useEffect(() => {
      activeToolRef.current = activeTool;
    }, [activeTool]);
    useEffect(() => {
      moveEnabledRef.current = moveEnabled;
    }, [moveEnabled]);
    const [compareChartData, setCompareChartData] = useState<
      Record<string, string>
    >({});

    const { chartData, isLoading, error } = useChartData(
      props.symbol,
      props.interval,
      props.period,
      props.token,
    );
    const onLoadingChangeRef = useRef(props.onLoadingChange);
    onLoadingChangeRef.current = props.onLoadingChange;
    useEffect(() => {
      onLoadingChangeRef.current?.(isLoading);
    }, [isLoading]);

    const onNoDataRef = useRef(props.onNoData);
    onNoDataRef.current = props.onNoData;
    const onErrorRef = useRef(props.onError);
    onErrorRef.current = props.onError;

    useEffect(() => {
      if (!isLoading && !chartData && !error) {
        onNoDataRef.current?.();
      }
    }, [isLoading, chartData, error]);

    useEffect(() => {
      if (!isLoading && error) {
        onErrorRef.current?.({ message: error, type: 'chart' });
      }
    }, [isLoading, error]);

    // Fetch CSV data for each comparison stock in parallel
    useEffect(() => {
      const { comparisonList, interval, period, token = '' } = props;
      if (comparisonList.length === 0) {
        setCompareChartData({});
        return;
      }

      let cancelled = false;
      const isMts = getPeriodType(interval) === 'mts';
      const today = new Date();
      const toYMD = formatDateYMD(today);
      const fromYMD = formatDateYMD(calcFromDate(period, today));

      const itemsToFetch = comparisonList.filter(
        (item) => item.symbol !== props.symbol,
      );

      Promise.allSettled(
        itemsToFetch.map((item) =>
          isMts
            ? getChartMtsData(
                item.symbol,
                interval,
                fromYMD + '0800',
                toYMD + '1630',
                token,
              )
            : getChartHtsData(item.symbol, interval, fromYMD, toYMD, token),
        ),
      ).then((results) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        itemsToFetch.forEach((item, i) => {
          const result = results[i];
          map[item.symbol] =
            result.status === 'fulfilled' ? (result.value ?? '') : '';
        });
        setCompareChartData(map);
      });

      return () => {
        cancelled = true;
      };
    }, [
      // eslint-disable-line react-hooks/exhaustive-deps
      props.comparisonList.map((i) => i.symbol).join(','),
      props.interval,
      props.period,
      props.token,
    ]);

    useEffect(() => {
      if (!isChartReady) return;
      if (isLoading) return;
      if (!chartData) return;
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;

      const message = buildMessage(
        { ...props, barColor: props.barColor ?? '', drawingConfigs },
        chartData,
        compareChartData,
      );
      iframe.contentWindow.postMessage(
        JSON.stringify({
          ...message,
          drawTool: getDrawTool(activeToolRef.current, moveEnabledRef.current),
        }),
        '*',
      );
      isInitializedRef.current = true;
    }, [
      readyVersion,
      isLoading,
      chartData,
      props.symbol,
      props.interval,
      props.period,
      props.chartType,
      props.candlestickColors,
      props.lineConfig,
      props.barColor,
      props.comparisonList,
      props.comparisonMode,
      compareChartData,
    ]); // eslint-disable-line react-hooks/exhaustive-deps

    // Partial update: TA only — does NOT re-initialize chart, preserving drawn trendlines
    useEffect(() => {
      if (!isInitializedRef.current) return;
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;
      const { overlay, indicator } = buildTAPayload(props.activeTA);
      iframe.contentWindow.postMessage(
        JSON.stringify({ source: CHART_SOURCE, overlay, indicator }),
        '*',
      );
    }, [props.activeTA]); // eslint-disable-line react-hooks/exhaustive-deps

    // Partial update: view settings — does NOT re-initialize chart, preserving drawn trendlines
    useEffect(() => {
      if (!isInitializedRef.current) return;
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;
      const { view } = props;
      iframe.contentWindow.postMessage(
        JSON.stringify({
          source: CHART_SOURCE,
          showMainChart: view.showMainChart,
          showTA: view.showTA,
          showScrollView: view.showTimelineScroller,
          showCrosshair: view.showCrosshair,
          showDrawTool: view.showTrendlines,
          showTracker: view.showTracker,
          showToolTips: view.showCrosshairTooltip,
        }),
        '*',
      );
    }, [props.view]); // eslint-disable-line react-hooks/exhaustive-deps

    // Partial update: active drawing tool selection
    useEffect(() => {
      if (!isInitializedRef.current) return;
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;
      const drawTool = getDrawTool(activeTool, moveEnabled);
      iframe.contentWindow.postMessage(
        JSON.stringify({ source: CHART_SOURCE, drawTool, showDrawTool: true }),
        '*',
      );
    }, [activeTool, moveEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    // Partial update: drawing tool style config
    useEffect(() => {
      if (!isInitializedRef.current) return;
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;
      iframe.contentWindow.postMessage(
        JSON.stringify({
          source: CHART_SOURCE,
          drawToolOption: buildDrawToolOption(drawingConfigs),
        }),
        '*',
      );
    }, [drawingConfigs]); // eslint-disable-line react-hooks/exhaustive-deps

    const sendToChart = useCallback((payload: Record<string, unknown>) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;
      if (payload.lineEditDone) {
        suppressDrawMenuRef.current = true;
        setTimeout(() => { suppressDrawMenuRef.current = false; }, 300);
      }
      iframe.contentWindow.postMessage(
        JSON.stringify({ source: CHART_SOURCE, ...payload }),
        '*',
      );
    }, []);

    // Print the bundle's own document instead of the host page: the host's layout
    // and print CSS can't add pages, and nothing on the host page needs hiding
    const print = useCallback(() => {
      const iframe = iframeRef.current;
      let doc: Document | null = null;
      try {
        doc = iframe?.contentDocument ?? null;
      } catch {
        doc = null;
      }
      const win = iframe?.contentWindow;
      if (!iframe || !doc || !win) return false;

      // Page sized to the chart exactly — one sheet, no scaling
      const widthMm = Math.ceil(iframe.offsetWidth / PX_PER_MM);
      const heightMm = Math.ceil(iframe.offsetHeight / PX_PER_MM);
      doc.getElementById(PRINT_PAGE_STYLE_ID)?.remove();
      const style = doc.createElement('style');
      style.id = PRINT_PAGE_STYLE_ID;
      style.textContent = `@media print {
  @page { size: ${widthMm}mm ${heightMm}mm; margin: 0; }
  html, body { margin: 0 !important; overflow: hidden !important; }
}`;
      doc.head.appendChild(style);
      win.addEventListener('afterprint', () => style.remove(), { once: true });
      win.focus();
      win.print();
      return true;
    }, []);

    useImperativeHandle(ref, () => ({ sendToChart, print }), [sendToChart, print]);

    // Use a ref so the persistent listener always sees the latest callback without re-registering
    const onDrawMenuChangeRef = useRef(props.onDrawMenuChange);
    useEffect(() => {
      onDrawMenuChangeRef.current = props.onDrawMenuChange;
    });

    // Single persistent listener for every bundle → host message (ready handshake,
    // drawn-line selection, scroll intent). Layout effect so it is attached in the
    // same commit that inserts the iframe — the bundle's 'ready' can't arrive first.
    useLayoutEffect(() => {
      // Cache the scroll target so we don't re-walk the DOM / force a layout
      // reflow (getComputedStyle + scrollHeight/clientHeight reads) on every
      // single scroll-intent message, which was causing jank while dragging.
      let scrollTargetResolved = false;
      let scrollTarget: HTMLElement | null = null;

      // Accumulate deltas and flush once per paint frame instead of writing
      // scrollTop synchronously on every message: at slow drag speed, small
      // noisy per-tick deltas written out of sync with the display's paint
      // cadence read as stutter rather than continuous motion.
      let pendingDelta = 0;
      let rafId: number | null = null;
      const flushScroll = () => {
        rafId = null;
        const delta = pendingDelta;
        pendingDelta = 0;
        if (delta === 0) return;
        if (!scrollTargetResolved) {
          scrollTarget = findScrollableAncestor(iframeRef.current);
          scrollTargetResolved = true;
        }
        if (scrollTarget) {
          scrollTarget.scrollTop += delta;
        } else {
          window.scrollBy(0, delta);
        }
      };

      const handleMessage = (e: MessageEvent) => {
        if (e.source !== iframeRef.current?.contentWindow) return;
        if (typeof e.data !== 'string') return;
        try {
          const msg = JSON.parse(e.data);
          if (msg?.source !== CHART_SOURCE) return;
          if (msg?.message === 'ready') {
            setReadyVersion((v) => v + 1);
            return;
          }
          if (msg?.currentTrendLineObj) {
            if (!suppressDrawMenuRef.current) {
              const raw = msg.currentTrendLineObj as DrawMenu;
              onDrawMenuChangeRef.current?.({
                ...raw,
                color: (raw.color ?? '').replace(/^#/, ''),
              });
            }
          }
          if (msg?.message === 'vertical-scroll-intent') {
            const deltaY = Number(msg.deltaY) || 0;
            if (deltaY !== 0) {
              pendingDelta += deltaY;
              if (rafId === null) {
                rafId = requestAnimationFrame(flushScroll);
              }
            }
          }
        } catch {
          /* ignore */
        }
      };
      window.addEventListener('message', handleMessage);
      return () => {
        window.removeEventListener('message', handleMessage);
        if (rafId !== null) cancelAnimationFrame(rafId);
      };
    }, []);

    // Main chart keeps 450px; each sub-chart indicator adds 150px
    const subChartCount = props.activeTA.filter(
      (ta) => !OVERLAY_IDS.has(ta.key),
    ).length;
    const iframeHeight = 450 + subChartCount * 150;

    // API failed, or the bundle is ready but the symbol has no data. Rendered as an
    // overlay so the iframe stays mounted (and stays ready) across symbol changes.
    const showError = !isLoading && (!!error || (isChartReady && !chartData));

    return (
      <div
        className="chart-view"
        style={{ height: iframeHeight, position: 'relative' }}
      >
        {isLoading && !props.onLoadingChange && (
          <div className="chart-view-loading">
            <Loading />
          </div>
        )}
        {showError && (
          <div
            className="chart-view-error"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--surface-primary)',
            }}
          >
            <ErrorMessage msg={errorMsg.find((m) => m.key === 'chart')} />
          </div>
        )}
        <iframe
          ref={iframeRef}
          className={`chart-iframe${props.iframeClassName ? ` ${props.iframeClassName}` : ''}`}
          style={{ height: iframeHeight, border: 'none', pointerEvents: props.blockPointerEvents ? 'none' : undefined }}
          src={bundleUrl}
          title="Advanced Chart"
          allow="fullscreen"
          width="100%"
        />
      </div>
    );
  },
);

ChartView.displayName = 'ChartView';

export default ChartView;

